import {handle} from 'redux-pack';

import {buildPreloginAmicaApiRequest} from 'shared/APIServices/utils/unauthenticatedAPIRequest';
import {buildErrorFromPayload} from 'types/Error';
import {UploadedFileData, UploadedFileObject} from 'types/FileUpload';
import {NOT_FOUND_STATUS_CODE} from 'utils/error';
import {isNewlyAddedFile, isFileAlreadyLoaded} from 'utils/FileUploader';

const FETCH_UPLOADED_FILE = 'fileUpload/FETCH_UPLOADED_FILE';
const START_FILE_UPLOAD = 'fileUpload/START_FILE_UPLOAD';
const FINISH_FILE_UPLOAD = 'fileUpload/FINISH_FILE_UPLOAD';
const FAILURE_FILE_UPLOAD = 'fileUpload/FAILURE_FILE_UPLOAD';
const REMOVE_FILE = 'fileUpload/REMOVE_FILE';
const FETCH_UPLOADED_FILE_DATA = 'fileUpload/FETCH_UPLOADED_FILE_DATA';
const CLEAR_REMOVED_FILE_ID = 'fileUpload/CLEAR_REMOVED_FILE_ID';
const UPDATE_FILE_PROGRESS = 'fileUpload/UPDATE_FILE_PROGRESS';
const UPDATE_PHOTO_DESCRIPTION = 'fileUpload/UPDATE_PHOTO_DESCRIPTION';
const STORE_ADDITIONAL_DETAILS = 'fileUpload/STORE_ADDITIONAL_DETAILS';
const UPDATE_UPLOADED_FILE_DATA = 'fileUpload/UPDATE_UPLOADED_FILE_DATA';
const RESET_HOME_PHOTO_UPLOAD_STATE = 'fileUpload/RESET_HOME_PHOTO_UPLOAD_STATE';
const INITIAL_UPLOAD_PROGRESS = 0;

export interface State {
  uploadedFilesByFileID: {
    [fileId: string]: UploadedFileObject;
  };
  uploadedFileData: UploadedFileData[];
  isRetrievingFileData: boolean;
  errorRetrievingFileData: number | null;
  removedFileId: string | null;
  additionalDetails: string;
}

export const initialState: State = Object.freeze({
  uploadedFilesByFileID: {},
  uploadedFileData: [],
  isRetrievingFileData: false,
  errorRetrievingFileData: null,
  removedFileId: null,
  additionalDetails: ''
});

const buildPhotoDescriptionFormData = ({version, description}: {version: string; description: string}) => {
  const homePhoto = {version, description};
  const formData = new FormData();
  formData.append(
    'homePhoto',
    new Blob([JSON.stringify(homePhoto)], {
      type: 'application/json'
    })
  );
  return formData;
};

export const removeFile = (
  photoId: string,
  claimsFileNumber: string,
  fileId: string,
  filename: string | null,
  filesize: string | null
) => ({
  type: REMOVE_FILE,
  promise: buildPreloginAmicaApiRequest({
    url: `/home-photos/${photoId}/claims/${claimsFileNumber}/files/${fileId}`,
    method: 'delete'
  }),
  meta: {
    fileId,
    filename,
    filesize
  }
});

export const fetchUploadedFileData = (photoId: string, claimsFileNumber: string) => ({
  type: FETCH_UPLOADED_FILE_DATA,
  promise: buildPreloginAmicaApiRequest({
    url: `/home-photos/${photoId}/claims/${claimsFileNumber}/files`,
    method: 'get'
  })
});

export const fetchUploadedFile = (photoId: string, claimsFileNumber: string, fileId: string) => ({
  type: FETCH_UPLOADED_FILE,
  promise: buildPreloginAmicaApiRequest({
    url: `/home-photos/${photoId}/claims/${claimsFileNumber}/files/${fileId}`,
    method: 'get',
    responseType: 'blob'
  }),
  meta: {
    fileId
  }
});

export const updatePhotoDescription = (
  photoId: string,
  claimsFileNumber: string,
  fileId: string,
  version: string,
  photoDescription: string
) => ({
  type: UPDATE_PHOTO_DESCRIPTION,
  promise: buildPreloginAmicaApiRequest({
    url: `/home-photos/${photoId}/claims/${claimsFileNumber}/files/${fileId}`,
    method: 'put',
    headers: {'Content-Type': 'multipart/form-data'},
    data: buildPhotoDescriptionFormData({version: version, description: photoDescription})
  }),
  meta: {fileId, version, photoDescription}
});

export const storeAdditionalDetails = (additionalDetails: string) => ({
  type: STORE_ADDITIONAL_DETAILS,
  meta: {additionalDetails}
});

export const updateUploadedFileData = () => ({
  type: UPDATE_UPLOADED_FILE_DATA
});

export const clearRemovedFileId = () => ({type: CLEAR_REMOVED_FILE_ID});

export const startFileUpload = (fileId: string, uploadPercentage: number) => ({
  type: START_FILE_UPLOAD,
  fileId: fileId,
  uploadPercentage: uploadPercentage
});

export const finishFileUpload = (fileId: string, uploadPercentage: number, data: any) => ({
  type: FINISH_FILE_UPLOAD,
  fileId: fileId,
  uploadPercentage: uploadPercentage,
  data: data
});

export const failureFileUpload = (fileId: string, error: any) => ({
  type: FAILURE_FILE_UPLOAD,
  fileId: fileId,
  payload: error
});

export const updateFileProgress = (fileId: string, uploadPercentage: number) => ({
  type: UPDATE_FILE_PROGRESS,
  fileId: fileId,
  uploadPercentage: uploadPercentage
});

export const resetHomePhotoUploadState = () => ({
  type: RESET_HOME_PHOTO_UPLOAD_STATE
});

const fileUpload = (state: State = initialState, action: any) => {
  const {type, payload} = action;
  switch (type) {
    case FETCH_UPLOADED_FILE_DATA:
      return handle(state, action, {
        start: (prevState) => ({...prevState, isRetrievingFileData: true, errorRetrievingFileData: null}),
        finish: (prevState) => ({...prevState, isRetrievingFileData: false, removedFileId: null}),
        failure: (prevState) => ({...prevState, errorRetrievingFileData: buildErrorFromPayload(payload).status}),
        success: (prevState) => ({...prevState, uploadedFileData: payload.data || [], uploadedFilesByFileID: {}})
      });
    case FETCH_UPLOADED_FILE:
      return handle(state, action, {
        start: (prevState) => ({
          ...prevState,
          uploadedFilesByFileID: {
            ...prevState.uploadedFilesByFileID,
            [action.meta.fileId]: {
              isLoadingFile: true,
              errorLoadingFile: null,
              isRemovingFile: false,
              errorRemovingFile: null,
              file: null
            }
          }
        }),
        finish: (prevState) => ({
          ...prevState,
          uploadedFilesByFileID: {
            ...prevState.uploadedFilesByFileID,
            [action.meta.fileId]: {
              ...prevState.uploadedFilesByFileID[action.meta.fileId],
              isLoadingFile: false
            }
          },
          removedFileId: null
        }),
        failure: (prevState) => ({
          ...prevState,
          uploadedFilesByFileID: {
            ...prevState.uploadedFilesByFileID,
            [action.meta.fileId]: {
              ...prevState.uploadedFilesByFileID[action.meta.fileId],
              errorLoadingFile: buildErrorFromPayload(payload)
            }
          }
        }),
        success: (prevState) => ({
          ...prevState,
          uploadedFilesByFileID: {
            ...prevState.uploadedFilesByFileID,
            [action.meta.fileId]: {
              ...prevState.uploadedFilesByFileID[action.meta.fileId],
              file: payload.data
            }
          }
        })
      });
    case REMOVE_FILE:
      return handle(state, action, {
        start: (prevState) => {
          const newUploadedFilesByFileID = prevState.uploadedFilesByFileID;
          const fileId = action.meta.fileId;
          const filenameAndSize = `${action.meta.filename}:${action.meta.filesize}`;
          if (newUploadedFilesByFileID[filenameAndSize]) {
            newUploadedFilesByFileID[filenameAndSize].isRemovingFile = true;
            newUploadedFilesByFileID[filenameAndSize].errorRemovingFile = null;
            newUploadedFilesByFileID[filenameAndSize].file = null;
          } else if (newUploadedFilesByFileID[fileId]) {
            newUploadedFilesByFileID[fileId].isRemovingFile = true;
            newUploadedFilesByFileID[fileId].errorRemovingFile = null;
            newUploadedFilesByFileID[fileId].file = null;
          }
          return {
            ...prevState,
            uploadedFilesByFileID: {...newUploadedFilesByFileID},
            removedFileId: null
          };
        },
        failure: (prevState) => {
          const newUploadedFilesByFileID = prevState.uploadedFilesByFileID;
          let newUploadedFileData = prevState.uploadedFileData;
          const fileId = action.meta.fileId;
          const filenameAndSize = `${action.meta.filename}:${action.meta.filesize}`;
          const fileNotFound = payload.response.status === NOT_FOUND_STATUS_CODE;
          const uploadingFile = newUploadedFilesByFileID[filenameAndSize];
          const uploadedFile = newUploadedFilesByFileID[fileId];
          const fileByFileId = !!uploadingFile ? filenameAndSize : fileId;
          if (fileNotFound) {
            if (uploadingFile) {
              delete newUploadedFilesByFileID[filenameAndSize];
            } else if (uploadedFile) {
              delete newUploadedFilesByFileID[fileId];
            }
            newUploadedFileData = prevState.uploadedFileData.filter(
              (file: UploadedFileData) => file.fileId !== fileId && file.fileId !== filenameAndSize
            );
          }
          return {
            ...prevState,
            uploadedFilesByFileID: fileNotFound
              ? newUploadedFilesByFileID
              : {
                  ...prevState.uploadedFilesByFileID,
                  [fileByFileId]: {
                    ...prevState.uploadedFilesByFileID[fileByFileId],
                    errorRemovingFile: buildErrorFromPayload(payload).status,
                    isRemovingFile: false
                  }
                },
            uploadedFileData: newUploadedFileData,
            removedFileId: null
          };
        },
        success: (prevState) => {
          const newUploadedFilesByFileID = prevState.uploadedFilesByFileID;
          const fileId = action.meta.fileId;
          const filenameAndSize = `${action.meta.filename}:${action.meta.filesize}`;
          let actualFileId = null;
          if (newUploadedFilesByFileID[filenameAndSize]) {
            delete newUploadedFilesByFileID[filenameAndSize];
            actualFileId = filenameAndSize;
          } else if (newUploadedFilesByFileID[fileId]) {
            delete newUploadedFilesByFileID[fileId];
          }
          const newUploadedFileData = prevState.uploadedFileData.filter(
            (file: UploadedFileData) => file.fileId !== fileId && file.fileId !== filenameAndSize
          );
          return {
            ...prevState,
            uploadedFilesByFileID: newUploadedFilesByFileID,
            uploadedFileData: newUploadedFileData,
            removedFileId: actualFileId
          };
        }
      });
    case CLEAR_REMOVED_FILE_ID:
      return {...state, removedFileId: null};
    case START_FILE_UPLOAD:
      return {
        ...state,
        uploadedFilesByFileID: {
          ...state.uploadedFilesByFileID,
          [action.fileId]: {
            isLoadingFile: true,
            errorLoadingFile: null,
            isRemovingFile: false,
            errorRemovingFile: null,
            file: null,
            uploadPercentage: INITIAL_UPLOAD_PROGRESS
          }
        }
      };
    case FINISH_FILE_UPLOAD:
      return {
        ...state,
        uploadedFilesByFileID: {
          ...state.uploadedFilesByFileID,
          [action.fileId]: {
            ...state.uploadedFilesByFileID[action.fileId],
            isLoadingFile: false,
            uploadPercentage: action.uploadPercentage,
            fileData: action.data
          }
        },
        removedFileId: null
      };
    case FAILURE_FILE_UPLOAD:
      return {
        ...state,
        uploadedFilesByFileID: {
          ...state.uploadedFilesByFileID,
          [action.fileId]: {
            ...state.uploadedFilesByFileID[action.fileId],
            isLoadingFile: false,
            errorLoadingFile: buildErrorFromPayload(payload)
          }
        }
      };
    case UPDATE_FILE_PROGRESS:
      return {
        ...state,
        uploadedFilesByFileID: {
          ...state.uploadedFilesByFileID,
          [action.fileId]: {
            ...state.uploadedFilesByFileID[action.fileId],
            uploadPercentage: action.uploadPercentage
          }
        }
      };
    case UPDATE_PHOTO_DESCRIPTION:
      return handle(state, action, {
        start: (prevState) => ({...prevState}),
        finish: (prevState) => ({...prevState}),
        failure: (prevState) => ({...prevState}),
        success: (prevState) => {
          const fileId = action.meta.fileId;
          const updatedUploadedFileData = prevState.uploadedFileData;
          const fileToUpdateIndex = prevState.uploadedFileData.findIndex(
            (file: UploadedFileData) => file.fileId === fileId
          );

          if (fileToUpdateIndex !== -1) {
            updatedUploadedFileData[fileToUpdateIndex].attributes = payload.data.attributes;
            updatedUploadedFileData[fileToUpdateIndex].version = payload.data.version;

            return {
              ...prevState,
              uploadedFileData: updatedUploadedFileData
            };
          }

          //Update the uploading collection if file was not found in uploaded collection, .entries returns an array of key-value pairs
          const updatedUploadedFilesByFileID = prevState.uploadedFilesByFileID;

          const uploadedFilesByFileID = Object.entries(prevState.uploadedFilesByFileID);
          const uploadedFileIndex = uploadedFilesByFileID.findIndex(
            (uploadedFile) => uploadedFile[1]?.fileData?.fileId === fileId
          );

          if (uploadedFileIndex !== -1) {
            const uploadedFileKey = uploadedFilesByFileID[uploadedFileIndex][0];
            updatedUploadedFilesByFileID[uploadedFileKey].fileData.attributes = payload.data.attributes;
            updatedUploadedFilesByFileID[uploadedFileKey].fileData.version = payload.data.version;

            return {
              ...prevState,
              uploadedFilesByFileID: updatedUploadedFilesByFileID
            };
          }

          return {...prevState};
        }
      });
    case STORE_ADDITIONAL_DETAILS:
      return {...state, additionalDetails: action.meta.additionalDetails || ''};
    case UPDATE_UPLOADED_FILE_DATA: {
      const newUploadedFileData = state.uploadedFileData;
      const newUploadingFilesByFileID = state.uploadedFilesByFileID;
      const newlyAddedFiles = Object.keys(state.uploadedFilesByFileID).filter((fileID) => isNewlyAddedFile(fileID));
      newlyAddedFiles.forEach((fileID) => {
        const newFile = state.uploadedFilesByFileID[fileID];
        if (!newFile.errorLoadingFile && !isFileAlreadyLoaded(state.uploadedFileData, newFile)) {
          newUploadedFileData.push(state.uploadedFilesByFileID[fileID].fileData);
        }
        delete newUploadingFilesByFileID[fileID];
      });
      return {...state, uploadedFileData: newUploadedFileData, uploadedFilesByFileID: newUploadingFilesByFileID};
    }
    case RESET_HOME_PHOTO_UPLOAD_STATE:
      return {...initialState};
    default:
      return state;
  }
};

export default fileUpload;
